import { useAuth0 } from "@auth0/auth0-react";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getTokenSilently } from "api/api";
import { get } from "http";
import { RootState } from "state";
import { School } from "types/School";
import { Student, StudentAggregateData } from "types/Student";

// const baseQuery = fetchBaseQuery({
//   baseUrl: process.env.REACT_APP_API_BASE_URL,
// });

// const baseQueryWithAuthToken = async (
//   args: any,
//   api: any,
//   extraOptions: any
// ) => {
//   const token = await getTokenSilently();

//   if (token) {
//     args.headers = {
//       ...args.headers,
//       Authorization: `Bearer ${token}`,
//     };
//   }

//   return baseQuery(args, api, extraOptions);
// };

interface iUpdateStudentAggregateData {
  studentId: number;
  aggregateData: StudentAggregateData;
}

export const api = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const state = getState() as RootState;
      const token = state.auth.token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  reducerPath: "api",
  tagTypes: [
    "Schools",
    "Students",
    "SchoolDashboard",
    "StudentDetail",
    "StudentReportPdf",
    "SchoolDetail",
    "SchoolTestData",
    "RecalculateScores",
    "",
  ],
  endpoints: (build) => ({
    getSchools: build.query<Array<School>, void>({
      query: () => "schools",
      providesTags: ["Schools"],
    }),
    getStudents: build.query<Array<Student>, number>({
      query: (schoolId: number) => `school/${schoolId}/students`,
      providesTags: ["Students"],
    }),
    getSchoolDashboard: build.query({
      query: (schoolId: number) => `school/dashboarddata/${schoolId}`,
      providesTags: ["SchoolDashboard"],
    }),
    getStudentDetail: build.query<Student, number>({
      query: (studentId: number) => `student/${studentId}`,
      providesTags: ["StudentDetail"],
    }),
    getStudentReportPdf: build.query<Blob, number>({
      query: (studentId: number) => ({
        url: `student/report/${studentId}`,
        responseType: "blob",
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
      providesTags: ["StudentReportPdf"],
    }),
    getSchoolDetail: build.query<School, number>({
      query: (schoolId: number) => `school/${schoolId}`,
      providesTags: ["SchoolDetail"],
    }),
    updateStudentAggregate: build.mutation<
      Student,
      iUpdateStudentAggregateData
    >({
      query: (requestData) => ({
        url: `student/${requestData.studentId}/aggregate`,
        method: "PUT",
        body: requestData.aggregateData,
      }),
      invalidatesTags: ["StudentDetail"],
    }),
    getSchoolTestData: build.mutation<Blob, number>({
      query: (schoolId: number) => ({
        url: `school/${schoolId}/DownloadTestData`,
        method: "POST",
        responseHandler: (response) => response.blob(),
      }),
    }),
    recalculateScores: build.mutation<void, number>({
      query: (schoolId: number) => ({
        url: `school/${schoolId}/RecalculateScores`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetStudentsQuery,
  useGetSchoolDashboardQuery,
  useGetStudentDetailQuery,
  useLazyGetStudentReportPdfQuery,
  useGetSchoolDetailQuery,
  useGetSchoolsQuery,
  useUpdateStudentAggregateMutation,
  useGetSchoolTestDataMutation,
  useRecalculateScoresMutation,
} = api;
